import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { useState, useEffect } from 'react'
import { Check, Loader2 } from 'lucide-react'
import { useLazyQuery, useQuery } from '@apollo/client'
import { GET_VIDEO_FRAMES } from '@/graphql/ads/video-thumbnail-queries'
import { GET_ASSET_STATUS } from '@/graphql/ads/ad-assets-queries'
import { CubeLoader } from '@/components/ui/cube-loader'
import ThumbnailUploader from '@/components/mediaBuying/adCreatives/ThumbnailUploader'
import ResponsiveImage from '@/components/mediaBuying/ResponsiveImage'

const ThumbnailGeneratingState = () => (
    <div className="tw:mt-8">
        <div className="tw:flex tw:justify-center tw:flex-col">
            <span className="tw:text-sm tw:font-semibold tw:mb-0.5 tw:flex tw:gap-2">
                <Loader2 className="tw:h-4 tw:w-4 tw:animate-spin" />
                Processing
            </span>
            <p className="tw:text-xs tw:flex tw:items-center">This usually takes 1-2 minutes...</p>
        </div>
    </div>
)

const ThumbnailPicker = ({ loading, thumbnails, selectedThumbnail, handleThumbnailSelect }) => {
    if (loading)
        return (
            <div className="tw:w-full tw:flex tw:items-center tw:justify-center">
                <CubeLoader size="default" fullScreen={true} />
            </div>
        )
    if (thumbnails.length === 0) return <div className="tw:text-sm tw:text-gray-600 tw:mt-4">No thumbnails found</div>

    return (
        <div className="tw:mt-6">
            {thumbnails.length > 0 && (
                <>
                    <span className="tw:text-sm tw:font-semibold mt-6">Choose a Thumbnail</span>
                    <div className="tw:grid tw:grid-cols-4 tw:gap-3 tw:max-h-96 tw:overflow-y-auto tw:mt-2">
                        {thumbnails.map((thumbnail) => (
                            <div
                                key={thumbnail.id}
                                onClick={() => handleThumbnailSelect(thumbnail)}
                                className="tw:relative tw:cursor-pointer"
                            >
                                <img
                                    src={`${import.meta.env.VITE_S3_BUCKET_URL}${thumbnail.thumbnail}`}
                                    alt={`Thumbnail at ${thumbnail.formattedTime}`}
                                    className="tw:!rounded-xs tw:w-full tw:h-auto"
                                />
                                {selectedThumbnail?.id === thumbnail.id && (
                                    <div className="tw:absolute tw:top-1 tw:right-1 tw:bg-blue-300 tw:text-white tw:rounded-full tw:w-6 tw:h-6 tw:flex tw:items-center tw:justify-center tw:text-sm">
                                        <Check size={16} />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    )
}

const VideoThumbnailEditor = ({ onSelectionChange, adAsset }) => {
    const [source, setSource] = useState('automatic')
    const [selectedThumbnail, setSelectedThumbnail] = useState(null)
    const [thumbnails, setThumbnails] = useState([])
    const [uploadState, setUploadState] = useState('idle')

    // Use Apollo polling for status checking
    const {
        data: statusData,
        stopPolling,
        startPolling,
    } = useQuery(GET_ASSET_STATUS, {
        variables: { id: adAsset?.id },
        skip: !adAsset?.id || adAsset?.type !== 'video',
        pollInterval: adAsset?.status === 'generating_frames' ? 2000 : 0,
        onCompleted: (data) => {
            const status = data?.AdAsset?.adAsset?.status

            // If status changed from generating to done, fetch thumbnails
            if (status === 'done' || (status === null && adAsset?.status === 'generating_frames')) {
                fetchThumbnails({ variables: { assetId: adAsset.id } })
            }
        },
    })

    const [fetchThumbnails, { loading }] = useLazyQuery(GET_VIDEO_FRAMES, {
        onCompleted: (data) => {
            if (data?.AdAsset?.videoFrames) {
                setThumbnails(data.AdAsset.videoFrames)
            }
        },
    })

    const currentStatus = statusData?.AdAsset?.adAsset?.status || adAsset?.status

    useEffect(() => {
        if (adAsset?.id && adAsset?.type === 'video') {
            if (currentStatus === 'generating_frames') {
                startPolling(2000)
            } else {
                stopPolling()
                fetchThumbnails({ variables: { assetId: adAsset.id } })
            }
        }

        return () => stopPolling()
    }, [adAsset?.id, currentStatus, fetchThumbnails, startPolling, stopPolling])

    // Auto-select first thumbnail when thumbnails are loaded
    useEffect(() => {
        if (thumbnails.length > 0 && onSelectionChange) {
            const firstThumbnail = thumbnails[0]
            // Always notify parent with the first thumbnail for default selection
            onSelectionChange({
                mode: source,
                selectedThumbnail: source === 'manual' ? selectedThumbnail : null,
                firstThumbnail: firstThumbnail,
                uploadState: uploadState,
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [thumbnails, source, selectedThumbnail, uploadState]) // Removed onSelectionChange to prevent infinite loop

    const handleThumbnailSelect = (thumbnail) => {
        setSelectedThumbnail(thumbnail)
        if (onSelectionChange) {
            onSelectionChange({
                mode: 'manual',
                selectedThumbnail: thumbnail,
                firstThumbnail: thumbnails.length > 0 ? thumbnails[0] : null,
                uploadState: uploadState,
            })
        }
    }

    const handleSourceChange = (newSource) => {
        setSource(newSource)

        // Notify parent component about the selection change
        if (onSelectionChange) {
            onSelectionChange({
                mode: newSource,
                selectedThumbnail: newSource === 'manual' ? selectedThumbnail : null,
                firstThumbnail: thumbnails.length > 0 ? thumbnails[0] : null,
                uploadState: uploadState,
            })
        }
    }

    const handleUploadStateChange = (newUploadState) => {
        setUploadState(newUploadState)

        // Notify parent component about upload state change
        if (onSelectionChange) {
            onSelectionChange({
                mode: source,
                selectedThumbnail: source === 'manual' ? selectedThumbnail : null,
                firstThumbnail: thumbnails.length > 0 ? thumbnails[0] : null,
                uploadState: newUploadState,
            })
        }
    }

    // Show generating state for video assets that are processing
    const isGeneratingThumbnails = adAsset?.type === 'video' && currentStatus === 'generating_frames'

    return (
        <div className="tw:space-y-6">
            <div className="tw:px-6">
                <RadioGroup defaultValue="automatic" onValueChange={handleSourceChange}>
                    <div className="tw:flex tw:items-center tw:!space-x-2">
                        <RadioGroupItem value="automatic" id="automatic" checked={source === 'automatic'} />
                        <Label htmlFor="automatic">Automatic</Label>
                    </div>
                    <div className="tw:flex tw:items-center tw:!space-x-2">
                        <RadioGroupItem value="manual" id="manual" checked={source === 'manual'} />
                        <Label htmlFor="manual">Manual</Label>
                    </div>
                    <div className="tw:flex tw:items-center tw:!space-x-2">
                        <RadioGroupItem value="upload" id="upload" checked={source === 'upload'} />
                        <Label htmlFor="upload">Upload</Label>
                    </div>
                </RadioGroup>

                {source === 'automatic' && (
                    <div className="tw:mt-6">
                        <span className="tw:text-sm tw:font-semibold mt-6">Thumbnail</span>
                        <ResponsiveImage
                            images={adAsset.thumbnails}
                            alt="Video thumbnail"
                            className="tw:max-w-120 tw:object-contain tw:mt-2 tw:rounded-xs"
                        />
                    </div>
                )}

                {source === 'manual' && (
                    <>
                        {isGeneratingThumbnails && <ThumbnailGeneratingState />}
                        {!isGeneratingThumbnails && (
                            <ThumbnailPicker
                                loading={loading}
                                thumbnails={thumbnails}
                                selectedThumbnail={selectedThumbnail}
                                handleThumbnailSelect={handleThumbnailSelect}
                            />
                        )}
                    </>
                )}
            </div>

            {source === 'upload' && (
                <ThumbnailUploader adCreativeAsset={adAsset} onUploadStateChange={handleUploadStateChange} />
            )}
        </div>
    )
}

export default VideoThumbnailEditor
